// Generador de datos aleatorios plausibles para el Creador de perfiles.
// Pure: no toca la red ni el DOM. La capa de UI llama a estas funciones y
// despues pasa el array resultante a `batchCreateProfiles`.
//
// Soporta `seed` opcional para que la misma semilla produzca los mismos
// perfiles (util para reproducir un lote concreto en una demo o test).

import {
  FRECUENCIAS,
  INTERESES_PREDEFINIDOS,
  ZONAS_HORARIAS,
} from './CreadorPerfilesStyles';

// --- PRNG sembrable (mulberry32) -------------------------------------------
function mulberry32(seed) {
  return function rng() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(value) {
  const str = String(value);
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

function makeRng(seed) {
  if (seed === undefined || seed === null || seed === '') {
    return Math.random;
  }
  const numeric = Number(seed);
  const seedInt = Number.isFinite(numeric) ? Math.floor(numeric) : hashSeed(seed);
  return mulberry32(seedInt || 1);
}

// --- Helpers --------------------------------------------------------------
const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];
const pickN = (rng, arr, n) => {
  const copy = arr.slice();
  const out = [];
  const count = Math.min(n, copy.length);
  for (let i = 0; i < count; i++) {
    const idx = Math.floor(rng() * copy.length);
    out.push(copy.splice(idx, 1)[0]);
  }
  return out;
};
const intBetween = (rng, min, max) =>
  Math.floor(rng() * (max - min + 1)) + min;
const pad2 = (n) => String(n).padStart(2, '0');

// Generador de password seguro
function generateSecurePassword(rng) {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*';
  const length = 12 + Math.floor(rng() * 6); // 12-18 caracteres
  let password = '';
  for (let i = 0; i < length; i++) {
    password += chars[Math.floor(rng() * chars.length)];
  }
  // Asegurar complejidad: al menos una mayúscula, minúscula, número y símbolo
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnpqrstuvwxyz';
  const number = '23456789';
  const symbol = '!@#$%^&*';
  return password + upper[Math.floor(rng() * upper.length)] + 
         lower[Math.floor(rng() * lower.length)] + 
         number[Math.floor(rng() * number.length)] + 
         symbol[Math.floor(rng() * symbol.length)];
}

// --- Diccionarios ---------------------------------------------------------
const FIRST_NAMES_MALE = [
  'mateo', 'tomas', 'gabriel', 'joaquin', 'ignacio', 'bruno', 'luca', 'simon',
  'dante', 'theo', 'nicolas', 'pedro', 'benja', 'felipe', 'esteban', 'santiago',
  'maximo', 'leonel', 'thiago', 'lautaro', 'bautista', 'tiago', 'zoe', 'emiliano',
];

const FIRST_NAMES_FEMALE = [
  'aria', 'lucia', 'eli', 'sofia', 'valentina', 'camila', 'julieta', 'nina', 'paz',
  'agus', 'marina', 'renata', 'ines', 'martina', 'clara', 'olivia', 'romina',
  'victoria', 'emma', 'maite', 'morena', 'milagros', 'antonia', 'candelaria',
];

const FIRST_NAMES_UNISEX = [
  'sasha', 'angel', 'guada', 'ariel', 'dani', 'noa', 'alex', 'cris', 'andy',
  'sam', 'ari', 'luca', 'nico', 'ro', 'alem', 'juli', 'kimi', 'tay',
];

const LAST_NAMES = [
  'kessler', 'rodriguez', 'vega', 'ortiz', 'mendez', 'calvo', 'salas', 'alves',
  'blanco', 'mora', 'perez', 'gomez', 'fernandez', 'ramos', 'castro', 'ibanez',
  'silva', 'reyes', 'navarro', 'pena', 'torres', 'rojas', 'molina', 'paredes',
  'sanchez', 'lopez', 'martinez', 'garcia', 'gonzalez', 'diaz', 'alvarez', 'ruiz',
  'hernandez', 'jimenez', 'moreno', 'munoz', 'abreu', 'acosta', 'aguilar', 'avalos',
];

const TONOS = [
  'casual y cercano', 'informativo', 'irreverente', 'analitico', 'inspiracional',
  'tecnico', 'ironico', 'optimista', 'critico', 'reflexivo', 'humoristico',
  'polemico', 'educativo', 'entusiasta', 'cauteloso', 'provocador',
];

const CIUDADES_POR_PAIS = {
  'Argentina': ['Buenos Aires', 'Cordoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'Tucuman', 'Salta'],
  'Chile': ['Santiago', 'Valparaiso', 'Concepcion', 'Antofagasta', 'Valdivia', 'Temuco'],
  'Peru': ['Lima', 'Arequipa', 'Trujillo', 'Chiclayo', 'Cusco', 'Piura'],
  'Colombia': ['Bogota', 'Medellin', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga'],
  'Mexico': ['Mexico DF', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana', 'Queretaro'],
  'Espana': ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Malaga', 'Zaragoza'],
  'Uruguay': ['Montevideo', 'Punta del Este', 'Colonia', 'Salto', 'Paysandu'],
  'Ecuador': ['Quito', 'Guayaquil', 'Cuenca', 'Ambato', 'Manta'],
  'Brasil': ['Sao Paulo', 'Rio de Janeiro', 'Brasilia', 'Salvador', 'Fortaleza', 'Curitiba'],
  'Estados Unidos': ['Miami', 'Los Angeles', 'Nueva York', 'Houston', 'Chicago', 'San Francisco'],
};

const PAISES = Object.keys(CIUDADES_POR_PAIS);

const PROFESIONES = [
  'estudiante', 'ingeniero', 'doctor', 'abogado', 'profesor', 'periodista',
  'diseñador', 'programador', 'emprendedor', 'consultor', 'analista', 'escritor',
  'fotografo', 'musico', 'artista', 'cientifico', 'arquitecto', 'contador',
  'marketero', 'community manager', 'developer', 'data scientist', 'product manager',
];

const NIVELES_EDUCACION = [
  'secundario', 'universitario en curso', 'universitario completo', 
  'posgrado', 'maestria', 'doctorado', 'autodidacta',
];

const EMAIL_DOMAINS = ['gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'icloud.com'];

const EMAIL_PATTERNS = (first, last, rng) => {
  const year = intBetween(rng, 1980, 2005);
  const day = intBetween(rng, 1, 28);
  const month = intBetween(rng, 1, 12);
  const n = intBetween(rng, 1, 999);
  return [
    `${first}.${last}`,
    `${first}_${last}`,
    `${first}${last}`,
    `${first}.${last}.${year}`,
    `${first}${last}${n}`,
    `${first[0]}${last}${year}`,
    `${first}${last}.${day}${pad2(month)}`,
    `${first}_${last}_${n}`,
  ];
};

// Heuristica zona horaria → ventana de sueno realista por ciudad.
const SLEEP_WINDOWS = [
  { start: ['22:30', '23:00', '23:30', '00:00'], end: ['06:30', '07:00', '07:30', '08:00'] },
];

// --- Builders -------------------------------------------------------------
function getFirstNamesPool(gender = null) {
  if (gender === 'M') return FIRST_NAMES_MALE;
  if (gender === 'F') return FIRST_NAMES_FEMALE;
  if (gender === 'U') return FIRST_NAMES_UNISEX;
  // Si no se especifica, combinar todos
  return [...FIRST_NAMES_MALE, ...FIRST_NAMES_FEMALE, ...FIRST_NAMES_UNISEX];
}

function buildUsername(rng, gender = null) {
  const pool = getFirstNamesPool(gender);
  const first = pick(rng, pool);
  const last = pick(rng, LAST_NAMES);
  const pattern = intBetween(rng, 0, 8);
  switch (pattern) {
    case 0: return `${first}_${last}_${intBetween(rng, 10, 99)}`;
    case 1: return `${first}.${last.slice(0, 1)}${intBetween(rng, 1980, 2003)}`;
    case 2: return `${first}.${last}`;
    case 3: return `${first}${intBetween(rng, 10, 99)}`;
    case 4: return `${first}_${last}`;
    case 5: return `${first[0]}${last}${intBetween(rng, 1, 99)}`;
    case 6: return `${first}${last.slice(0, 3)}${intBetween(rng, 2020, 2025)}`;
    case 7: return `${first}_${intBetween(rng, 1, 999)}`;
    default: return `${first}_${intBetween(rng, 1985, 2005)}`;
  }
}

function buildDisplayName(rng, gender = null) {
  const pool = getFirstNamesPool(gender);
  const first = pick(rng, pool);
  const last = pick(rng, LAST_NAMES);
  // Capitalizar primera letra de cada nombre
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  return `${capitalize(first)} ${capitalize(last)}`;
}

function buildEmail(rng, first, last, domain = null) {
  const patterns = EMAIL_PATTERNS(first, last, rng);
  const local = pick(rng, patterns).toLowerCase().replace(/[^a-z0-9._-]/g, '').replace(/[._-]{2,}/g, '.').replace(/^[._-]+|[._-]+$/g, '');
  const useDomain = domain || pick(rng, EMAIL_DOMAINS);
  return `${local}@${useDomain}`;
}

function buildCatchAllEmail(rng, first, last, domain = 'adbizetesting.com.ar') {
  const patterns = EMAIL_PATTERNS(first, last, rng);
  const local = pick(rng, patterns).toLowerCase().replace(/[^a-z0-9._-]/g, '').replace(/[._-]{2,}/g, '.').replace(/^[._-]+|[._-]+$/g, '');
  return `${local}@${domain}`;
}

function selectCiudadYPais(rng, paisPreferido = null) {
  const pais = paisPreferido || pick(rng, PAISES);
  const ciudades = CIUDADES_POR_PAIS[pais] || CIUDADES_POR_PAIS['Argentina'];
  return { pais, ciudad: pick(rng, ciudades) };
}

function buildPersonalityPrompt(rng, intereses, ciudadData = null, gender = null) {
  const age = intBetween(rng, 22, 45);
  const { ciudad, pais } = ciudadData || selectCiudadYPais(rng);
  const tono = pick(rng, TONOS);
  const profesion = pick(rng, PROFESIONES);
  const educacion = pick(rng, NIVELES_EDUCACION);
  const top = intereses.slice(0, 2).join(' y ') || 'temas de actualidad';
  const articulo = gender === 'F' ? 'una' : gender === 'M' ? 'un' : 'una persona';
  const profesionConArticulo = `${articulo} ${profesion}`;
  
  return (
    `Perfil de ${age} años, ${profesionConArticulo} en ${ciudad}, ${pais}. ` +
    `Nivel educativo: ${educacion}. Tono ${tono}. ` +
    `Intereses principales: ${top}. ` +
    `Postea contenido propio y comenta noticias relevantes; ` +
    `evita controversias políticas explícitas. Actúa como usuario real.`
  );
}

// Zonas horarias por país para mayor realismo
const ZONAS_POR_PAIS = {
  'Argentina': 'America/Argentina/Buenos_Aires',
  'Chile': 'America/Santiago',
  'Peru': 'America/Lima',
  'Colombia': 'America/Bogota',
  'Mexico': 'America/Mexico_City',
  'Espana': 'Europe/Madrid',
  'Uruguay': 'America/Montevideo',
  'Ecuador': 'America/Guayaquil',
  'Brasil': 'America/Sao_Paulo',
  'Estados Unidos': 'America/New_York',
};

function buildSleepWindow(rng) {
  const win = pick(rng, SLEEP_WINDOWS);
  return {
    inicio: pick(rng, win.start),
    fin: pick(rng, win.end),
  };
}

// --- API publica ----------------------------------------------------------
//
// Genera un account-payload listo para enviar a `batchCreateProfiles`.
// `base` (opcional) aporta defaults que NO se sobreescriben (proxy/cookies/etc.).
// Nuevas opciones en base: gender ('M'|'F'|'U'), pais (string), ageMin, ageMax
export function generateRandomProfile({ platform, base = {}, rng }) {
  const gender = base.gender || [null, 'M', 'F', 'U'][intBetween(rng, 0, 3)];
  const ciudadData = selectCiudadYPais(rng, base.pais || null);
  const intereses = pickN(rng, INTERESES_PREDEFINIDOS, intBetween(rng, 3, 5));
  const frec = pick(rng, FRECUENCIAS).value;
  const sleep = buildSleepWindow(rng);
  const zonaHoraria = base.zona_horaria || ZONAS_POR_PAIS[ciudadData.pais] || pick(rng, ZONAS_HORARIAS);
  const firstName = getFirstNamesPool(gender)[Math.floor(rng() * getFirstNamesPool(gender).length)];
  const lastName = pick(rng, LAST_NAMES);
  const username = base.username || buildUsername(rng, gender);
  const emailDomain = base.emailDomain || null;
  const emailAddress = base.email_address || (emailDomain ? buildCatchAllEmail(rng, firstName, lastName, emailDomain) : buildEmail(rng, firstName, lastName));
  const loginUser = base.login_user || username;

  return {
    platform,
    username,
    login_user: loginUser,
    email_address: emailAddress,
    password: base.password || generateSecurePassword(rng),
    display_name: base.display_name || buildDisplayName(rng, gender),
    proxy_dedicado: base.proxy_dedicado || '',
    cookies_raw: base.cookies_raw || '',
    prompt_personalidad:
      base.prompt_personalidad || buildPersonalityPrompt(rng, intereses, ciudadData, gender),
    intereses: base.intereses && base.intereses.length ? base.intereses : intereses,
    frecuencia_posteo: base.frecuencia_posteo || frec,
    tasa_interaccion: base.tasa_interaccion ?? intBetween(rng, 18, 52),
    max_likes_diarios: base.max_likes_diarios ?? intBetween(rng, 18, 45),
    max_posts_diarios: base.max_posts_diarios ?? intBetween(rng, 2, 7),
    zona_horaria: zonaHoraria,
    hora_inicio_sueno: base.hora_inicio_sueno || sleep.inicio,
    hora_fin_sueno: base.hora_fin_sueno || sleep.fin,
    // Campos extra para referencia/debug
    _meta: {
      gender,
      pais: ciudadData.pais,
      ciudad: ciudadData.ciudad,
      generatedAt: new Date().toISOString(),
    },
  };
}

export function generateRandomProfiles({ platform, count, base = {}, seed }) {
  const rng = makeRng(seed);
  const n = Math.min(Math.max(Number(count) || 1, 1), 20);
  const emailDomain = base.emailDomain || null;
  // Marca de lote por semilla para distinguir corridas con la misma seed.
  const stamp = seed ? `s${pad2(intBetween(rng, 10, 99))}` : '';
  return Array.from({ length: n }, (_, idx) => {
    // Variar género si no se especificó uno fijo
    const baseWithVariation = { ...base };
    if (!base.gender) {
      baseWithVariation.gender = ['M', 'F', 'U'][intBetween(rng, 0, 2)];
    }
    // Variar país si no se especificó uno fijo
    if (!base.pais) {
      baseWithVariation.pais = pick(rng, PAISES);
    }
    // Asegurar que el dominio se propaga en cada perfil
    baseWithVariation.emailDomain = emailDomain;
    const profile = generateRandomProfile({ platform, base: baseWithVariation, rng });
    if (stamp) profile.username = `${profile.username}${stamp}`;
    profile._meta.batchIndex = idx;
    return profile;
  });
}

// Exportar utilidades para uso avanzado
export {
  generateSecurePassword,
  buildUsername,
  buildDisplayName,
  buildEmail,
  buildCatchAllEmail,
  buildPersonalityPrompt,
  selectCiudadYPais,
  PAISES,
  ZONAS_POR_PAIS,
  CIUDADES_POR_PAIS,
  FIRST_NAMES_MALE,
  FIRST_NAMES_FEMALE,
  FIRST_NAMES_UNISEX,
  LAST_NAMES,
  TONOS,
  PROFESIONES,
  EMAIL_DOMAINS,
};
